import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({
    selector: 'input[appDateInputMask]',
    standalone: false
})
export class DateInputMaskDirective {
  private isUpdating = false;

  constructor(private elementRef: ElementRef<HTMLInputElement>) { }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent): void {
    if (this.isUpdating || event.isComposing) {
      return;
    }

    const input = this.elementRef.nativeElement;
    const cursorPosition = input.selectionStart ?? input.value.length;
    const digitsBeforeCursor = this.countDigits(input.value.slice(0, cursorPosition));
    const formattedValue = this.formatPartialValue(input.value);
    const nextCursorPosition = this.shouldKeepCursorAfterSeparator(input.value, cursorPosition)
      ? formattedValue.length
      : this.findCursorPosition(formattedValue, digitsBeforeCursor);

    this.updateInputValue(formattedValue, nextCursorPosition);
  }

  @HostListener('blur')
  onBlur(): void {
    const input = this.elementRef.nativeElement;
    const formattedValue = this.formatCompleteValue(input.value);

    if (formattedValue && formattedValue !== input.value) {
      this.updateInputValue(formattedValue, formattedValue.length);
    }
  }

  private updateInputValue(value: string, cursorPosition: number): void {
    const input = this.elementRef.nativeElement;

    if (input.value === value) {
      return;
    }

    this.isUpdating = true;
    input.value = value;
    input.setSelectionRange(cursorPosition, cursorPosition);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    this.isUpdating = false;
  }

  private formatPartialValue(value: string): string {
    if (this.isCompleteIsoDate(value)) {
      return this.formatCompactDigits(this.normaliseDigits(value));
    }

    if (/[./-]/.test(value)) {
      return this.formatSeparatedPartialValue(value);
    }

    return this.formatCompactDigits(this.normaliseDigits(value));
  }

  private formatCompactDigits(value: string): string {
    const digits = value.slice(0, 8);

    if (digits.length <= 2) {
      return digits;
    }

    if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }

  private formatCompleteValue(value: string): string | null {
    const separatedValue = this.formatSeparatedCompleteValue(value);

    if (separatedValue) {
      return separatedValue;
    }

    const digits = this.normaliseDigits(value);

    if (digits.length !== 6 && digits.length !== 8) {
      return null;
    }

    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.length === 6 ? `20${digits.slice(4)}` : digits.slice(4);

    return `${day}/${month}/${year}`;
  }

  private formatSeparatedPartialValue(value: string): string {
    const dateParts = value.split(/[./-]/).slice(0, 3);

    if (dateParts.length === 2 && dateParts[0].length === 2 && dateParts[1].replace(/\D/g, '').length > 2) {
      return this.formatCompactDigits(this.normaliseDigits(value));
    }

    const formattedParts = dateParts.map((part, index) => {
      const maxLength = index === 2 ? 4 : 2;
      return part.replace(/\D/g, '').slice(0, maxLength);
    });

    return formattedParts.join('/');
  }

  private formatSeparatedCompleteValue(value: string): string | null {
    const dateParts = value.trim().split(/[./-]/);

    if (
      dateParts.length !== 3 ||
      !dateParts.every((part) => /^\d+$/.test(part))
    ) {
      return null;
    }

    if (dateParts[0].length === 4) {
      const [year, month, day] = dateParts;
      return this.toFormattedDate(year, month, day);
    }

    const [day, month, year] = dateParts;

    if (year.length !== 2 && year.length !== 4) {
      return null;
    }

    return this.toFormattedDate(this.expandYear(year), month, day);
  }

  private toFormattedDate(year: string, month: string, day: string): string {
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year.padStart(4, '0')}`;
  }

  private expandYear(year: string): string {
    return year.length === 2 ? `20${year}` : year;
  }

  private normaliseDigits(value: string): string {
    const isoDateMatch = value.trim().match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);

    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      return `${day.padStart(2, '0')}${month.padStart(2, '0')}${year}`;
    }

    return value.replace(/\D/g, '');
  }

  private isCompleteIsoDate(value: string): boolean {
    return /^\d{4}[./-]\d{1,2}[./-]\d{1,2}$/.test(value.trim());
  }

  private shouldKeepCursorAfterSeparator(value: string, cursorPosition: number): boolean {
    return cursorPosition === value.length && /[./-]$/.test(value);
  }

  private countDigits(value: string): number {
    return value.replace(/\D/g, '').length;
  }

  private findCursorPosition(value: string, digitsBeforeCursor: number): number {
    if (digitsBeforeCursor === 0) {
      return 0;
    }

    let digitsSeen = 0;

    for (let index = 0; index < value.length; index++) {
      if (/\d/.test(value[index])) {
        digitsSeen++;
      }

      if (digitsSeen === digitsBeforeCursor) {
        return index + 1;
      }
    }

    return value.length;
  }
}
